import { type ApolloError } from "@apollo/client";
import { useCallback, useMemo, useState } from "react";
import {
  useCreateTestApiOrderMutation,
  useTestIntegrationOrderCompaniesQuery,
  useTestIntegrationOrderContactsQuery,
  type TestApiOrderResult,
} from "../../../generated/graphql";
import { throwOnGraphqlErrors } from "../../api-keys-admin/throw-on-graphql-errors";
import {
  type ApiOrderFormState,
  createDefaultPackage,
  createEmptyApiOrderForm,
  type PackageFormState,
  toCreateTestApiOrderInput,
  validateApiOrderForm,
} from "../api-order-form";

/** One entry of a company, contact, or service-level picker. */
export type ApiOrderOption = {
  readonly uuid: string;
  readonly name: string;
};

/**
 * Writes one top-level form field. Kept generic so a section component sets its
 * own fields without the panel declaring a handler per field.
 */
export type SetApiOrderField = <TKey extends keyof ApiOrderFormState>(
  key: TKey,
  next: ApiOrderFormState[TKey],
) => void;

export type UseApiOrderFormResult = {
  readonly form: ApiOrderFormState;
  readonly companyOptions: readonly ApiOrderOption[];
  readonly contactOptions: readonly ApiOrderOption[];
  readonly serviceOptions: readonly ApiOrderOption[];
  readonly companiesLoading: boolean;
  readonly contactsLoading: boolean;
  readonly companiesError: ApolloError | undefined;
  readonly contactsError: ApolloError | undefined;
  readonly submitting: boolean;
  readonly outcome: TestApiOrderResult | null;
  readonly error: string | null;
  readonly setField: SetApiOrderField;
  readonly setCompany: (companyUuid: string) => void;
  readonly setBillingContact: (billingContactUuid: string) => void;
  readonly setPackage: (next: PackageFormState) => void;
  readonly addPackage: () => void;
  readonly removePackage: (packageId: string) => void;
  readonly submit: () => Promise<void>;
  readonly reset: () => void;
};

/**
 * Owns the API-order tab's form state, its picker queries, and the create
 * mutation, so the panel below is left composing sections.
 *
 * Contacts are refetched with `network-only` because an operator switching
 * companies needs the contact list the target environment holds right now, not
 * one cached from an earlier company.
 *
 * @returns The form state, picker options, and the actions a section calls.
 */
const useApiOrderForm = (): UseApiOrderFormResult => {
  const [form, setForm] = useState<ApiOrderFormState>(createEmptyApiOrderForm);
  const [outcome, setOutcome] = useState<TestApiOrderResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const {
    data: companiesData,
    error: companiesError,
    loading: companiesLoading,
  } = useTestIntegrationOrderCompaniesQuery();
  const {
    data: contactsData,
    error: contactsError,
    loading: contactsLoading,
  } = useTestIntegrationOrderContactsQuery({
    variables: { companyUuid: form.companyUuid },
    skip: form.companyUuid === "",
    fetchPolicy: "network-only",
  });
  const [createTestApiOrder, { loading: submitting }] =
    useCreateTestApiOrderMutation();

  const companyOptions = useMemo(
    () =>
      (companiesData?.companies ?? []).map((company) => ({
        uuid: company.uuid,
        name: company.name,
      })),
    [companiesData],
  );
  const contactOptions = useMemo(
    () =>
      (contactsData?.companyContacts ?? []).map((contact) => ({
        uuid: contact.uuid,
        name: contact.displayName,
      })),
    [contactsData],
  );
  const serviceOptions = useMemo(
    () =>
      (contactsData?.companyContacts ?? []).find(
        (contact) => contact.uuid === form.billingContactUuid,
      )?.services ?? [],
    [contactsData, form.billingContactUuid],
  );

  const setField = useCallback<SetApiOrderField>((key, next) => {
    setForm((current) => ({ ...current, [key]: next }));
  }, []);

  const setCompany = useCallback((companyUuid: string) => {
    setForm((current) => ({
      ...current,
      companyUuid,
      // The contacts and service levels below belong to the previous company,
      // so they cannot carry over.
      billingContactUuid: "",
      serviceLevelUuid: "",
    }));
  }, []);

  const setBillingContact = useCallback((billingContactUuid: string) => {
    setForm((current) => ({
      ...current,
      billingContactUuid,
      serviceLevelUuid: "",
    }));
  }, []);

  const setPackage = useCallback((next: PackageFormState) => {
    setForm((current) => ({
      ...current,
      packages: current.packages.map((packageForm) =>
        packageForm.id === next.id ? next : packageForm,
      ),
    }));
  }, []);

  const addPackage = useCallback(() => {
    setForm((current) => ({
      ...current,
      packages: [...current.packages, createDefaultPackage()],
    }));
  }, []);

  const removePackage = useCallback((packageId: string) => {
    setForm((current) => ({
      ...current,
      packages: current.packages.filter(
        (packageForm) => packageForm.id !== packageId,
      ),
    }));
  }, []);

  const submit = useCallback(async () => {
    setError(null);
    setOutcome(null);
    const problem = validateApiOrderForm(form);
    if (problem !== null) {
      setError(problem);
      return;
    }
    try {
      const result = await createTestApiOrder({
        variables: { input: toCreateTestApiOrderInput(form) },
      });
      throwOnGraphqlErrors(result);
      const created = result.data?.createTestApiOrder ?? null;
      if (created === null) {
        setError("The mutation returned no result.");
        return;
      }
      setOutcome(created);
    } catch (submitError) {
      setError(
        submitError instanceof Error
          ? submitError.message
          : "Creating the order failed.",
      );
    }
  }, [createTestApiOrder, form]);

  const reset = useCallback(() => {
    setForm(createEmptyApiOrderForm());
    setOutcome(null);
    setError(null);
  }, []);

  return {
    form,
    companyOptions,
    contactOptions,
    serviceOptions,
    companiesLoading,
    contactsLoading,
    companiesError,
    contactsError,
    submitting,
    outcome,
    error,
    setField,
    setCompany,
    setBillingContact,
    setPackage,
    addPackage,
    removePackage,
    submit,
    reset,
  };
};

export default useApiOrderForm;
